<template>
  <div class="flex flex-col gap-2 h-full overflow-hidden">
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <button
          class="btn btn-outline btn-sm"
          @click="clickSendMail(MailSenderStatus.Draft)"
        >
          {{ $t(`Buttons.saveDraft`) }}
        </button>
        <button
          class="btn btn-primary btn-sm"
          @click="clickSendMail(MailSenderStatus.Sent)"
        >
          {{ $t(`Buttons.send`) }}
        </button>
      </template>
    </ThreadsNav>
    <div class="flex-1 overflow-auto">
      <div
        class="text-sm text-base-content/70 px-4 flex items-center gap-2 mb-2"
        v-if="mateMail.parent"
      >
        <i class="iconify mdi--reply-outline transform -rotate-180"></i>
        <span>回复</span>
        <span>{{ mateMail.parent?.subject }}</span>
      </div>
      <VanCellGroup inset>
        <!-- 名字 -->
        <VanField
          label="发件人"
          :model-value="mateStore.mateIdMap.get(mateMail.senderId)?.name"
          :placeholder="$t('mail.selectSenderFirst')"
          :rules="[
            { required: true, message: $t('User.mate.basicInfo.rules.name') },
          ]"
          required
          is-link
          readonly
          @click="clickMateSelect"
        />
        <VanField
          v-if="mateMail.senderId"
          label="收件人"
          :model-value="mateMail.recipient?.name"
          :placeholder="$t('mail.selectRecipientFirst')"
          :rules="[
            { required: true, message: $t('User.mate.basicInfo.rules.name') },
          ]"
          required
          :is-link="!loading"
          :disabled="loading"
          readonly
          @click="clickFriendMateSelect"
        >
          <template #right-icon v-if="loading">
            <i class="loading loading-ring loading-xs"></i>
          </template>
        </VanField>

        <!-- 标题 -->
        <template v-if="mateMail.senderId && mateMail.recipient">
          <VanField
            label="标题"
            v-model="mateMail.subject"
            :placeholder="$t('mail.subject')"
            required
          />

          <CustomRichTextCell
            title="填写信件内容"
            :ai-type="AIWriteType.MateMail"
            :content="mateMail.body"
            :cb="onContentUpdate"
            :cb2="(title:string) => (mateMail.subject = title)"
            :options="{
              senderId: mateMail.senderId,
              recipientId: mateMail.recipientId,
              subject: mateMail.subject,
            }"
          />

          <VanCell
            v-if="
              mateMail.senderId && mateStore.getMyMateById(mateMail.senderId)
            "
          >
            <VoiceGenerate
              :mate="mateStore.getMyMateById(mateMail.senderId)!"
              :text="removeHtmlTag(mateMail.body)"
              tone-type="voice"
              @generate="onGenerateAudio"
            />
          </VanCell>
        </template>
      </VanCellGroup>
    </div>

    <ModalFriendMateSelect
      v-model:show="showFriendMateSelect"
      :sender="mateMail.sender"
      @select="onFriendMateSelect"
      :show-my-mate="false"
    />
  </div>
</template>

<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { useInteractive } from "@/stores/interactive";
import { useUI } from "@/stores/ui";
import { Mate, MateMail, Media } from "@/types/entity";
import { onMounted, ref } from "vue";
import { MailSenderStatus } from "@/enums/mission";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { useRoute, useRouter } from "vue-router";
import { AIWriteType } from "@/enums/mateEnums";
import { ToastWrapperInstance } from "vant/es";
import { removeHtmlTag } from "@/tools/text";

const mateMail = ref<MateMail>({} as MateMail);
const mateStore = useMate();
const interactiveStore = useInteractive();
const route = useRoute();
const router = useRouter();
const onContentUpdate = (content: string) => {
  mateMail.value.body = content;
};

const loading = ref(false);
const uiStore = useUI();
const showFriendMateSelect = ref(false);
const clickMateSelect = () => {
  if (mateMail.value.parent) return showFailToast("回复信件，不能选择发件人");

  uiStore.popMateSelector({
    mateOnly: true,
    switchMate: false,
    exclude: [mateMail.value.senderId],
    title: "选择当前身份",
    callback: async (mate: Mate) => {
      uiStore.showMateSelector = false;
      mateMail.value.senderId = mate.id;
      mateMail.value.sender = mate;
      mateMail.value.recipient = undefined;
    },
  });
};

const clickFriendMateSelect = () => {
  if (mateMail.value.parent) return showFailToast("回复信件，不能选择收件人");
  showFriendMateSelect.value = true;
};

const onFriendMateSelect = (mate: Mate) => {
  mateMail.value.recipientId = mate.id;
  mateMail.value.recipient = mate;
  showFriendMateSelect.value = false;
};

const clickSendMail = async (senderStatus: MailSenderStatus) => {
  let { parentId, senderId, recipientId, subject, body, audioMediaId } =
    mateMail.value;

  audioMediaId = audioMediaId ? Number(audioMediaId) : undefined;

  if (!senderId) return showFailToast("请选择发件人");
  if (!recipientId) return showFailToast("请选择收件人");
  if (!subject) return showFailToast("请输入标题");
  if (!body) return showFailToast("请输入信件内容");
  let loading: ToastWrapperInstance | undefined;
  interactiveStore.homeNeedReload = true;
  try {
    if (mateMail.value.uuid) {
      if (senderStatus === MailSenderStatus.Draft) {
        loading = showLoadingToast("草稿保存中...");
      } else {
        loading = showLoadingToast("信件发送中...");
      }
      await interactiveStore.updateMail(mateMail.value.id, {
        senderId,
        recipientId,
        subject,
        body,
        audioMediaId,
        senderStatus,
      });
      if (senderStatus === MailSenderStatus.Sent) {
        showSuccessToast("信件发送成功");
        setTimeout(() => {
          router.back();
        }, 1000);
      } else if (senderStatus === MailSenderStatus.Draft) {
        loading?.close();
        showSuccessToast("草稿保存成功");
      }
    } else {
      loading = showLoadingToast("信件发送中...");
      const mail = await interactiveStore.createMail({
        parentId,
        senderId,
        recipientId,
        subject,
        body,
        audioMediaId,
        senderStatus,
      });
      if (senderStatus === MailSenderStatus.Draft) {
        mateMail.value.uuid = mail.uuid;
        mateMail.value.id = mail.id;
        showSuccessToast("草稿保存成功");
        loading?.close();
      } else {
        showSuccessToast("信件发送成功");
        setTimeout(() => {
          router.back();
        }, 1000);
      }
    }
  } catch {
    loading?.close();
  }
};

const onGenerateAudio = (media?: Media) => {
  if (!media) return;

  mateMail.value.audioMediaId = media?.id;
  mateMail.value.audioMedia = media;
};

onMounted(async () => {
  let { uuid, parentUuid, senderId, recipientId, recipientName } = route.query;
  if (uuid) {
    const mail = await interactiveStore.getMail(uuid as string);
    console.log(mail);
    if (mail) {
      mateMail.value = mail;
      if (mateMail.value.parent) {
        parentUuid = mateMail.value.parent.uuid;
      }
    }
  }
  if (parentUuid) {
    // mateMail.value.parentId = Number(parentUuid);
    const parentMail = await interactiveStore.getMail(parentUuid as string);
    if (parentMail) {
      mateMail.value.parentId = parentMail.id;
      mateMail.value.parent = parentMail;
      mateMail.value.senderId = parentMail.recipientId;
      mateMail.value.recipientId = parentMail.senderId;
      mateMail.value.recipient = parentMail.sender;
      if (parentMail.subject.startsWith("回复：")) {
        mateMail.value.subject = parentMail.subject;
      } else {
        mateMail.value.subject = "回复：" + parentMail.subject;
      }
    }
  }

  if (senderId) {
    mateMail.value.senderId = Number(senderId);
  }
  if (recipientId && recipientName) {
    mateMail.value.recipientId = Number(recipientId);
    mateMail.value.recipient = {
      id: Number(recipientId),
      name: recipientName as string,
    } as Mate;
  }
});
</script>
